import { IMAGE_WIDTH_SNAP } from './image-height';
import type { ImageBlock, ImageIntrinsicSize } from './types';

export const IMAGE_WIDTH_MIN_PX = 16;

function storedImageWidthPx(block: ImageBlock): number | null {
  return block.widthPx ?? null;
}

export { storedImageWidthPx };

export function clampImageWidthPx(px: number, max: number): number {
  return Math.min(max, Math.max(IMAGE_WIDTH_MIN_PX, px));
}

export function snapImageWidthPx(
  px: number,
  max: number,
  fine: boolean,
): number {
  const step = fine ? 1 : IMAGE_WIDTH_SNAP;
  return clampImageWidthPx(Math.round(px / step) * step, max);
}

export function isImageDefaultWidth(block: ImageBlock): boolean {
  return storedImageWidthPx(block) === null;
}

export function effectiveImageBoxWidthPx(
  block: ImageBlock,
  contentMaxWidth: number,
): number {
  const widthPx = storedImageWidthPx(block);
  if (contentMaxWidth <= 0) {
    return widthPx ?? 0;
  }
  if (widthPx !== null) {
    return clampImageWidthPx(widthPx, contentMaxWidth);
  }
  return contentMaxWidth;
}

/** Full column width is stored as null; narrower widths are fixed px. */
export function resolveImageWidthCommit(
  widthPx: number,
  contentMaxWidth: number,
): number | null {
  if (contentMaxWidth <= 0) {
    return widthPx;
  }
  return widthPx >= contentMaxWidth ? null : widthPx;
}

/** Default px width from natural pixel width; null means full column. */
export function defaultImageWidthPxFromIntrinsic(
  intrinsicSize: ImageIntrinsicSize | null,
  contentMaxWidth: number,
): number | null {
  if (contentMaxWidth <= 0 || intrinsicSize === null || intrinsicSize.width <= 0) {
    return null;
  }
  if (intrinsicSize.width >= contentMaxWidth) {
    return null;
  }
  return clampImageWidthPx(intrinsicSize.width, contentMaxWidth);
}

/** First custom width when switching from full column width. */
export function initialCustomImageWidthPx(
  intrinsicSize: ImageIntrinsicSize | null,
  contentMaxWidth: number,
): number {
  const fromIntrinsic = defaultImageWidthPxFromIntrinsic(intrinsicSize, contentMaxWidth);
  if (fromIntrinsic !== null) {
    return fromIntrinsic;
  }
  if (contentMaxWidth <= IMAGE_WIDTH_MIN_PX) {
    return IMAGE_WIDTH_MIN_PX;
  }
  const maxCustom = Math.max(IMAGE_WIDTH_MIN_PX, contentMaxWidth - 1);
  return clampImageWidthPx(Math.round(contentMaxWidth * 0.75), maxCustom);
}

export type ImageWidthCssParts = {
  width: string;
  maxWidth: string;
};

export function imageBlockWidthCssParts(
  block: ImageBlock,
  contentMaxWidth: number,
  editorMode: boolean,
): ImageWidthCssParts {
  const maxWidth = '100%';
  const widthPx = storedImageWidthPx(block);

  if (widthPx !== null) {
    const px =
      contentMaxWidth > 0
        ? clampImageWidthPx(widthPx, contentMaxWidth)
        : widthPx;
    return { width: `${px}px`, maxWidth };
  }

  if (editorMode && contentMaxWidth > 0) {
    return { width: `${contentMaxWidth}px`, maxWidth };
  }

  return { width: '100%', maxWidth };
}

export function imageBlockWidthCss(
  block: ImageBlock,
  contentMaxWidth: number,
  editorMode: boolean,
): string {
  const parts = imageBlockWidthCssParts(block, contentMaxWidth, editorMode);
  return `width:${parts.width};max-width:${parts.maxWidth};`;
}

export function imageBlockShellWidth(
  block: ImageBlock,
  contentMaxWidth: number,
  editorMode: boolean,
): string {
  return imageBlockWidthCssParts(block, contentMaxWidth, editorMode).width;
}
